/**
 * features/students/AlumniView.tsx
 * ------------------------------------------------------------------
 * Read-only register of former students — anyone marked `status: 'left'`
 * (graduated / withdrawn / transferred by the Promotion Wizard or from a
 * Student profile) OR still parked in an archived class. This is the
 * proper home for the records the old "Alumni class" was standing in for.
 *
 * Read-only. Gated canManageStudents (admin / academic admin).
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { GraduationCap, Search } from 'lucide-react';
import { useData } from '@/contexts/DataContext';

const REASON_LABEL: Record<string, string> = {
  graduated: 'Graduated',
  withdrawn: 'Withdrawn',
  transferred: 'Transferred',
};

export default function AlumniView() {
  const { students, classes } = useData();
  const classById = useMemo(() => new Map(classes.map((c) => [c.id, c])), [classes]);
  const archivedIds = useMemo(
    () => new Set(classes.filter((c) => c.archived).map((c) => c.id)),
    [classes],
  );

  const alumni = useMemo(
    () =>
      students
        .filter((s) => s.status === 'left' || archivedIds.has(s.currentClass))
        .map((s) => {
          const lastClass = classById.get(s.lastClassId || s.currentClass);
          return {
            id: s.id,
            fullName: s.fullName,
            admissionNumber: s.admissionNumber || '',
            lastClassName: lastClass?.name || '—',
            reason: s.exitReason ? REASON_LABEL[s.exitReason] : archivedIds.has(s.currentClass) && s.status !== 'left' ? 'Archived class' : 'Left',
            exitDate: s.exitDate ? new Date(s.exitDate).toLocaleDateString('en-GB') : '',
            guardian: s.parentContact?.guardianName || s.admissions?.guardians?.find((g) => g.isPrimaryContact)?.fullName || '',
            guardianContact: s.parentContact?.email || s.parentContact?.whatsapp || s.admissions?.guardians?.find((g) => g.isPrimaryContact)?.email || '',
          };
        })
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students, archivedIds, classById],
  );

  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return alumni;
    return alumni.filter(
      (a) => a.fullName.toLowerCase().includes(needle) || a.admissionNumber.toLowerCase().includes(needle) || a.lastClassName.toLowerCase().includes(needle),
    );
  }, [alumni, q]);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] text-white p-6">
        <div className="flex items-center gap-3">
          <div className="bg-white/10 rounded-xl p-2">
            <GraduationCap size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold">Alumni &amp; former students</h1>
            <p className="text-sm opacity-90">
              Students who have graduated, withdrawn or transferred — kept for records and excluded from active rosters.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {alumni.length} former student{alumni.length === 1 ? '' : 's'}
          </h2>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / admission no / class…"
              className="w-full sm:w-72 pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
            />
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="p-5 text-sm text-gray-500 dark:text-gray-400">
            {alumni.length === 0
              ? 'No former students recorded yet. They appear here once marked left (Promotion Wizard or a Student profile), or if their class is archived.'
              : 'No matches.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Adm. no</th>
                  <th className="px-4 py-2.5">Last class</th>
                  <th className="px-4 py-2.5">Reason</th>
                  <th className="px-4 py-2.5">Left</th>
                  <th className="px-4 py-2.5">Guardian</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-b border-gray-50 dark:border-gray-700/50 last:border-0">
                    <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{a.fullName}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.admissionNumber || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.lastClassName}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.reason}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.exitDate || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                      {a.guardian}
                      {a.guardianContact ? <span className="text-gray-400"> · {a.guardianContact}</span> : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
